import { FilterOption, Options, Table } from '@ckprivate/ckf-ui';
import { ActionIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableColumn } from 'mantine-datatable';
import React, { useState } from 'react';

import { useI18n } from '../../../../contexts/I18nProvider';
import EndOfMonthService from '../../../../services/EndOfMonthService';
import type { CompanyResponse } from '../../../../types/api/response/company';
import {
  type DossierResponseFromOpenpaye,
  SynchroOpenpayeParams,
} from '../../../../types/api/response/module';
import handleErrorMessage from '../../../../utils/handleErrorMessage';
import {
  getFilter,
  getHiddenColumns,
  saveFilter,
  saveHiddenColumns,
} from '../../../../utils/optionsPreferences';

type Props = {
  customerCompany: CompanyResponse;
  accountantCompanyId: string;
  openpayeModuleParams: SynchroOpenpayeParams;
};

const componentName = 'ChooseDossierToSyncToExistingClient';

export default function ChooseDossierToSyncToExistingClient({
  customerCompany,
  accountantCompanyId,
  openpayeModuleParams,
}: Props) {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const columns: DataTableColumn<DossierResponseFromOpenpaye>[] = [
    {
      accessor: 'dossierId',
      title: t('w.id'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'code',
      title: t('w.code'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'nomDossier',
      title: t('w.name'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'siret',
      title: t('w.siret'),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'actions',
      title: t('w.actions'),
      textAlign: 'right',
      sortable: false,
      width: '0%',
      render: (dossier) => (
        <Tooltip label={t('w.synchronize')}>
          <ActionIcon
            variant="transparent"
            onClick={() => processDossierEmployeeSync(dossier)}
            loading={isLoading}
          >
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  const [options, setOptions] = useState<Options>({
    sort: { columnAccessor: 'nomDossier', direction: 'asc' },
    search: {
      accessor: 'nomDossier',
      label: t('w.name'),
    },
    hiddenColumns: getHiddenColumns({ componentName }),
    updateHiddenColumns,
    filter: getFilter({
      componentName,
      filter: [],
    }),
    updateFilter,
  });

  function updateFilter(newFilter: FilterOption[]) {
    const _options = { ...options };
    _options.filter = newFilter;
    saveFilter({ componentName, filter: newFilter });
    setOptions(_options);
  }

  function updateHiddenColumns(newHiddenColumns: string[]) {
    const _options = { ...options };
    _options.hiddenColumns = newHiddenColumns;
    saveHiddenColumns({ componentName, hiddenColumns: newHiddenColumns });
    setOptions(_options);
  }

  const {
    mutate: synchroOpenpayeDossierEmployeesToExistingClientCompany,
    isLoading,
  } = useMutation({
    mutationFn: (payload: {
      accountantCompanyId: string;
      clientCompanyId: string;
      dossierId: string;
    }) =>
      EndOfMonthService.synchroOpenpayeDossierEmployeesToExistingClientCompany(
        payload.accountantCompanyId,
        payload.clientCompanyId,
        payload.dossierId
      ),
    onSuccess: () => {
      showNotification({
        id: 'createClientFromDossier-success',
        message: t('w.success'),
        color: 'green',
        icon: <IconCheck />,
      });

      queryClient.invalidateQueries({
        queryKey: [
          'CharteredAccountantService.accountantCustomersList',
          accountantCompanyId,
        ],
      });

      modals.closeAll();
    },
    onError: (error) => {
      showNotification({
        id: 'createClientCompanyFromOpenpayeDossier-error',
        title: t('w.error'),
        message: handleErrorMessage(error, t),
        icon: <IconCheck />,
        color: 'red',
      });
    },
  });

  function processDossierEmployeeSync(dossier: DossierResponseFromOpenpaye) {
    showNotification({
      id: 'synchroDossierEmployeesToExistingClient-loading',
      message: t('w.treatmentInProgress'),
      color: 'green',
      icon: <IconCheck />,
    });
    synchroOpenpayeDossierEmployeesToExistingClientCompany({
      accountantCompanyId: accountantCompanyId,
      clientCompanyId: customerCompany.id,
      dossierId: dossier.dossierId + '',
    });
  }

  return (
    <Table
      options={options}
      rows={openpayeModuleParams.dossiers || []}
      columns={columns}
    />
  );
}
